import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LargeTitle, Surface, ListGroup, ListItem, AButton, Pill } from "@/components/ios";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, Upload, Trash2, Building2 } from "lucide-react";
import { compressImage } from "@/lib/image";

const ACCENTS = [
  { name: "Monochrome", color: "#0A0A0A" },
  { name: "iOS Blue", color: "#007AFF" },
  { name: "Harbour Teal", color: "#30B0C7" },
  { name: "Indigo", color: "#5856D6" },
  { name: "Emerald", color: "#34C759" },
  { name: "Ruby", color: "#FF3B30" },
];

const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });

export default function Settings() {
  const { user, agency, setAgency, loadAgency } = useAuth();
  const { dark, toggle } = useTheme();
  const [team, setTeam] = useState([]);
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [saving, setSaving] = useState(false);
  const canEdit = ["principal", "admin"].includes(user?.role);

  useEffect(() => { api.get("/team").then(({ data }) => setTeam(data)); }, []);
  useEffect(() => { if (agency) { setName(agency.name || ""); setInitials(agency.brand_initials || ""); } }, [agency]);

  const changeRole = async (uid, role) => {
    setTeam((t) => t.map((m) => (m.id === uid ? { ...m, role } : m)));
    try {
      await api.patch(`/team/${uid}/role`, { role });
      toast.success("Role updated");
    } catch (e) {
      toast.error("Could not update role");
      api.get("/team").then(({ data }) => setTeam(data));
    }
  };

  const save = async (patch, msg = "Branding updated") => {
    setSaving(true);
    try {
      const { data } = await api.put("/agency", patch);
      setAgency(data);
      if (data?.accent_color) document.documentElement.style.setProperty("--accent", data.accent_color);
      toast.success(msg);
    } catch (e) { toast.error(canEdit ? "Could not save" : "Only principals/admins can change branding"); loadAgency(); }
    finally { setSaving(false); }
  };

  const setAccent = (color) => {
    document.documentElement.style.setProperty("--accent", color);
    save({ accent_color: color }, "Brand colour updated");
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const raw = await readFile(file);
      const small = await compressImage(raw, 400, 0.85);
      await save({ logo_url: small }, "Logo updated");
    } catch (err) { toast.error("Could not process image"); }
    e.target.value = "";
  };

  return (
    <div>
      <LargeTitle subtitle="Agency branding, appearance and team">Settings</LargeTitle>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          <div>
            <div className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary">Brand identity</div>
            <Surface className="p-4 space-y-4" data-testid="brand-identity">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-2xl grid place-items-center overflow-hidden shrink-0 accent-fg" style={{ background: agency?.logo_url ? "var(--surface-2)" : "var(--accent)", border: "1px solid var(--border)" }}>
                  {agency?.logo_url ? <img src={agency.logo_url} alt="logo" className="h-full w-full object-contain" /> : (initials || <Building2 size={22} />)}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium mb-1.5" style={{ color: "var(--text)" }}>Agency logo</div>
                  <div className="flex gap-2">
                    <label data-testid="logo-upload" className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer inline-flex items-center gap-1.5 ${canEdit ? "" : "opacity-50 pointer-events-none"}`} style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
                      <Upload size={13} /> Upload
                      <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={!canEdit} />
                    </label>
                    {agency?.logo_url && (
                      <button data-testid="logo-remove" onClick={() => save({ logo_url: "" }, "Logo removed")} disabled={!canEdit}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 border" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                        <Trash2 size={13} /> Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-secondary">Display name</label>
                  <input data-testid="brand-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit}
                    className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
                </div>
                <div>
                  <label className="text-xs text-secondary">Initials</label>
                  <input data-testid="brand-initials" value={initials} maxLength={3} onChange={(e) => setInitials(e.target.value.toUpperCase())} disabled={!canEdit}
                    className="w-full mt-1 text-sm rounded-lg px-3 py-2 outline-none border uppercase" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
                </div>
              </div>
              <AButton testid="save-brand" disabled={!canEdit || saving} onClick={() => save({ name: name.trim(), brand_initials: initials })} className="w-full">
                {saving ? "Saving…" : "Save identity"}
              </AButton>
            </Surface>
          </div>

          <div>
            <div className="px-4 pb-2 text-xs font-semibold uppercase tracking-wide text-secondary">Brand accent colour</div>
            <Surface className="p-4">
              <div className="grid grid-cols-3 gap-3">
                {ACCENTS.map((a) => (
                  <button key={a.color} data-testid={`accent-${a.color}`} onClick={() => setAccent(a.color)} disabled={!canEdit}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl transition-transform active:scale-95" style={{ background: "var(--surface-2)" }}>
                    <div className="h-9 w-9 rounded-full grid place-items-center" style={{ background: a.color }}>
                      {agency?.accent_color?.toUpperCase() === a.color.toUpperCase() && <Check size={16} className="text-white" />}
                    </div>
                    <span className="text-xs" style={{ color: "var(--text)" }}>{a.name}</span>
                  </button>
                ))}
              </div>
              <label className="mt-3 flex items-center justify-between text-sm" style={{ color: "var(--text)" }}>
                <span>Custom colour</span>
                <input data-testid="accent-custom" type="color" value={agency?.accent_color || "#0A0A0A"} disabled={!canEdit}
                  onChange={(e) => setAccent(e.target.value)} className="h-8 w-14 rounded cursor-pointer bg-transparent" />
              </label>
            </Surface>
          </div>

          <ListGroup header="Tenant response window">
            <ListItem right={<Switch data-testid="enforce-deadline-switch" checked={!!agency?.enforce_tenant_deadline} disabled={!canEdit}
              onCheckedChange={(v) => save({ enforce_tenant_deadline: v }, "Response window updated")} />}>
              <div><span style={{ color: "var(--text)" }}>Enforce response deadline</span>
                <div className="text-xs text-secondary">Block tenant responses after the deadline (off = open until you close it)</div></div>
            </ListItem>
            <ListItem right={
              <input data-testid="deadline-days" type="number" min="1" disabled={!canEdit} defaultValue={agency?.tenant_response_deadline_days || 7}
                onBlur={(e) => save({ tenant_response_deadline_days: parseInt(e.target.value) || 7 }, "Deadline updated")}
                className="w-16 text-sm rounded-lg px-2 py-1.5 outline-none border text-right" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />}>
              <span style={{ color: "var(--text)" }}>Response window (days)</span>
            </ListItem>
          </ListGroup>

          <ListGroup header="Appearance">
            <ListItem right={<Switch checked={dark} onCheckedChange={toggle} data-testid="dark-switch" />}><span style={{ color: "var(--text)" }}>Dark mode</span></ListItem>
          </ListGroup>

          <ListGroup header="Data & Privacy">
            <ListItem right={<Pill tone="success">ap-southeast-2</Pill>}><span style={{ color: "var(--text)" }}>Data residency</span></ListItem>
            <ListItem right={<Pill tone="success">Enabled</Pill>}><span style={{ color: "var(--text)" }}>Encryption at rest & in transit</span></ListItem>
            <ListItem right={<Pill tone="success">7 years</Pill>}><span style={{ color: "var(--text)" }}>Media retention</span></ListItem>
          </ListGroup>
        </div>

        <div>
          <ListGroup header={`Team · ${agency?.name || ""}`}>
            {team.map((m) => (
              <ListItem key={m.id} right={
                canEdit ? (
                  <select
                    data-testid={`role-select-${m.id}`}
                    value={m.role}
                    onChange={(e) => changeRole(m.id, e.target.value)}
                    className="text-xs rounded-lg px-2 py-1.5 outline-none border capitalize"
                    style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}
                  >
                    <option value="principal">principal</option>
                    <option value="admin">admin</option>
                    <option value="property_manager">property manager</option>
                    <option value="inspector">inspector</option>
                    <option value="read_only_auditor">read only auditor</option>
                  </select>
                ) : <Pill tone="accent">{m.role.replace(/_/g, " ")}</Pill>
              }>
                <div><div className="font-medium" style={{ color: "var(--text)" }}>{m.name}</div><div className="text-xs text-secondary">{m.email}</div></div>
              </ListItem>
            ))}
          </ListGroup>
        </div>
      </div>
    </div>
  );
}
